import { useMemo, useState, type ReactElement } from 'react';
import { HardDrive, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/shell/PageHeader';

import {
  useAdminS3ConfigMutations,
  useAdminS3Configs,
  type S3ConfigInput,
  type S3ConfigViewModel,
} from './api';
import { DeleteS3ConfigConfirmDialog } from './components/DeleteS3ConfigConfirmDialog';
import { S3ConfigFormDialog } from './components/S3ConfigFormDialog';
import { S3ConfigsTable } from './components/S3ConfigsTable';

export function AdminS3ConfigsPage(): ReactElement {
  const { t } = useTranslation();
  const query = useAdminS3Configs();
  const { create, update, remove } = useAdminS3ConfigMutations();

  const [formOpen, setFormOpen] = useState(false);
  const [formInitial, setFormInitial] = useState<S3ConfigViewModel | null>(null);
  const [deleteRow, setDeleteRow] = useState<S3ConfigViewModel | null>(null);

  const rows = useMemo(
    () => (query.isSuccess ? [...query.data].sort((a, b) => a.name.localeCompare(b.name)) : []),
    [query.isSuccess, query.data],
  );

  const isEdit = formInitial !== null;
  const formPending = isEdit ? update.isPending : create.isPending;
  const formError = isEdit ? update.error : create.error;

  function closeForm(): void {
    setFormOpen(false);
    setFormInitial(null);
    create.reset();
    update.reset();
  }

  function handleFormSubmit(input: S3ConfigInput): void {
    if (formInitial !== null) {
      update.mutate({ ...input, id: formInitial.id }, { onSuccess: closeForm });
    } else {
      create.mutate(input, { onSuccess: closeForm });
    }
  }

  function handleDeleteConfirm(row: S3ConfigViewModel): void {
    remove.mutate(row.id, {
      onSuccess: () => {
        setDeleteRow(null);
        remove.reset();
      },
    });
  }

  return (
    <>
      <PageHeader
        icon={<HardDrive className="h-[18px] w-[18px]" aria-hidden="true" />}
        title={t('routes.admin.s3.heading')}
        subtitle={t('routes.admin.s3.subheading')}
      />
      <div className="px-8 py-5 space-y-8 bg-background">
        <section aria-labelledby="admin-s3-configs-heading" className="space-y-4">
          <header className="flex items-start justify-between gap-4">
            <div>
              <h2 id="admin-s3-configs-heading" className="text-base font-semibold text-foreground">
                {t('routes.admin.s3.configs.heading')}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('routes.admin.s3.configs.subheading')}
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => {
                create.reset();
                update.reset();
                setFormInitial(null);
                setFormOpen(true);
              }}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t('routes.admin.s3.configs.addAction')}
            </Button>
          </header>

          {query.isPending && <RowsSkeleton />}

          {query.isError && (
            <div className="max-w-xl">
              <FetchErrorCallout
                error={query.error}
                heading={t('routes.admin.s3.configs.error.heading')}
                message={t('routes.admin.s3.configs.error.message')}
                codeLabel={t('routes.admin.s3.configs.error.codeLabel')}
                onRetry={() => {
                  query.refetch().catch(() => undefined);
                }}
                retryLabel={t('routes.admin.s3.configs.error.retry')}
              />
            </div>
          )}

          {query.isSuccess && rows.length === 0 && (
            <p className="text-sm text-muted-foreground" role="status">
              {t('routes.admin.s3.configs.empty')}
            </p>
          )}

          {query.isSuccess && rows.length > 0 && (
            <DataTable aria-label={t('routes.admin.s3.configs.heading')}>
              <S3ConfigsTable
                rows={rows}
                onEdit={(row) => {
                  create.reset();
                  update.reset();
                  setFormInitial(row);
                  setFormOpen(true);
                }}
                onDelete={(row) => {
                  remove.reset();
                  setDeleteRow(row);
                }}
                pendingDeleteId={remove.isPending && deleteRow !== null ? deleteRow.id : null}
              />
            </DataTable>
          )}
        </section>
      </div>

      <S3ConfigFormDialog
        open={formOpen}
        initial={formInitial}
        onOpenChange={(next) => {
          if (!next && formPending) return;
          if (!next) closeForm();
          else setFormOpen(true);
        }}
        onSubmit={handleFormSubmit}
        pending={formPending}
        submitError={formError}
      />

      <DeleteS3ConfigConfirmDialog
        row={deleteRow}
        onCancel={() => {
          if (remove.isPending) return;
          setDeleteRow(null);
          remove.reset();
        }}
        onConfirm={handleDeleteConfirm}
        pending={remove.isPending}
        submitError={remove.error}
      />
    </>
  );
}

function RowsSkeleton(): ReactElement {
  return (
    <div className="space-y-2" aria-hidden="true">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full rounded-md" />
      ))}
    </div>
  );
}
